import { useMemo } from 'react';
import { noop, keyBy } from 'lodash-es';
import { Typography, useStateInRef } from 'seagull';
import { useStore, useStoreActions } from "../../../../contexts";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { useOnMount } from "../../../../hooks/useOnMount";
import { getAccountingDetails } from "../../../../integrations/Accounting/accounting.utils";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../../models";
import { RecurrencePeriodTypes } from "../../../../models/service-billing/model";
import { BusinessChannelEventBinder, InvoiceEventType, PusherEvent } from "../../../../pusher";
import { selectors } from "../../../../store/state-selectors";
import { Accounting } from "../../../../types/api.model";
import styles from "./BillingControl.module.scss";
import { applyChangesToService, applyDiscount, getPauseDisabledTooltip } from "./BillingControl.utils";
import AutomaticServiceBillingItem from "./LineItems/AutomaticServiceBillingItem";
import CreditBillingItem from "./LineItems/CreditBillingItem";
import ManualInStagesBillingItem from "./LineItems/ManualInStagesBillingItem/ManualInStagesBillingItem";
import ManualServiceBillingItem from "./LineItems/ManualServiceBillingItem";
import MapServicesCallout from "./MapServicesCallout";
import BillingControlSection from "./ServicesSection";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function BillingControl(props) {
  var _qboItems$data2, _xeroItems$data2;
  const {
    invoiceDateInfo,
    agreement,
    billingControlData,
    onRefresh,
    earliestBillableDate
  } = props;
  const {
    services
  } = agreement;
  const getProps = useStateInRef(props);
  const onBindToUserChannel = props.onBindToUserChannel || noop;
  const isQboLinked = useStore(selectors.integration.showQboIntegration);
  const {
    fetchQboTimeActivities
  } = useStoreActions();
  const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
  const isLinkedToXero = useStore(selectors.integration.isLinkedToXero);
  const {
    XERO_INTEGRATION
  } = useFeatureFlags();
  const qboItems = useStore(state => state.qboItems);
  const xeroItems = useStore(state => state.xeroItems);
  const qboClasses = useStore(state => state.qboClasses);
  const qboItemsById = useMemo(() => {
    var _qboItems$data;
    return keyBy((_qboItems$data = qboItems.data) === null || _qboItems$data === void 0 ? void 0 : _qboItems$data.items, 'id');
  }, [(_qboItems$data2 = qboItems.data) === null || _qboItems$data2 === void 0 ? void 0 : _qboItems$data2.items]);
  const xeroItemsById = useMemo(() => {
    var _xeroItems$data;
    return keyBy((_xeroItems$data = xeroItems.data) === null || _xeroItems$data === void 0 ? void 0 : _xeroItems$data.items, 'id');
  }, [(_xeroItems$data2 = xeroItems.data) === null || _xeroItems$data2 === void 0 ? void 0 : _xeroItems$data2.items]);
  const qboClassesById = useMemo(() => keyBy(qboClasses.data, 'id'), [qboClasses.data]);
  useOnMount(() => {
    if (isQboLinked) fetchQboTimeActivities(agreement.id);
  });
  const {
    lineItemsPerSection,
    hasItems
  } = useMemo(() => {
    const {
      onRefresh
    } = getProps();
    const lineItemsPerSection = {
      manual: {
        oneTime: [],
        oneTimeInStages: [],
        [RecurrencePeriodTypes.Weekly]: [],
        [RecurrencePeriodTypes.BiWeekly]: [],
        [RecurrencePeriodTypes.Monthly]: [],
        [RecurrencePeriodTypes.Quarterly]: [],
        [RecurrencePeriodTypes.Yearly]: []
      },
      recurring: {
        onApproval: [],
        [RecurrencePeriodTypes.Weekly]: [],
        [RecurrencePeriodTypes.BiWeekly]: [],
        [RecurrencePeriodTypes.Monthly]: [],
        [RecurrencePeriodTypes.Quarterly]: [],
        credit: []
      }
    };
    const servicesToDisplay = services.map(applyChangesToService).filter(s => !serviceModel.hasUnbillableStatus(s)).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    servicesToDisplay.forEach(service => {
      var _serviceModel$startBi, _qboClassesById$class, _qboItemsById$qboInte, _xeroItemsById$xeroIn;
      const {
        name,
        id,
        cost,
        billing
      } = service;
      const {
        costDisplay,
        unitCapDisplay
      } = serviceCostModel.stringifyServiceCost(cost);
      const startBillingAt = (_serviceModel$startBi = serviceModel.startBillingAt(service)) !== null && _serviceModel$startBi !== void 0 ? _serviceModel$startBi : undefined;
      const classId = serviceModel.integratedClassId(service);
      const qboIntegratedId = serviceModel.integratedId({
        service,
        accounting: Accounting.QBO
      });
      const xeroIntegratedId = serviceModel.integratedId({
        service,
        accounting: Accounting.Xero
      });
      const qboClassName = classId ? (_qboClassesById$class = qboClassesById[classId]) === null || _qboClassesById$class === void 0 ? void 0 : _qboClassesById$class.name : undefined;
      const qboItemName = qboIntegratedId ? (_qboItemsById$qboInte = qboItemsById[qboIntegratedId]) === null || _qboItemsById$qboInte === void 0 ? void 0 : _qboItemsById$qboInte.name : undefined;
      const xeroItemName = xeroIntegratedId ? (_xeroItemsById$xeroIn = xeroItemsById[xeroIntegratedId]) === null || _xeroItemsById$xeroIn === void 0 ? void 0 : _xeroItemsById$xeroIn.name : undefined;
      serviceBillingModel.accept(billing, {
        visitOngoing: billing => {
          const disabled = invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected;
          const category = lineItemsPerSection.recurring[billing.recurrencePeriod];
          category.push( /*#__PURE__*/_jsx(AutomaticServiceBillingItem, {
            idx: category.length,
            billing: billing,
            disabled: disabled,
            agreement: agreement,
            serviceId: id,
            serviceName: name,
            qboItemName: qboItemName,
            xeroItemName: xeroItemName,
            costDisplay: costDisplay,
            tooltipTitle: disabled ? getPauseDisabledTooltip(false) : undefined,
            qboClassName: qboClassName,
            startBillingAt: startBillingAt
          }, service.id));
        },
        visitOnApproval: billing => {
          const category = lineItemsPerSection.recurring.onApproval;
          category.push( /*#__PURE__*/_jsx(AutomaticServiceBillingItem, {
            idx: category.length,
            billing: billing,
            disabled: true,
            agreement: agreement,
            serviceId: id,
            serviceName: name,
            qboItemName: qboItemName,
            xeroItemName: xeroItemName,
            costDisplay: costDisplay,
            tooltipTitle: "This one-time service will be billed on the agreement's effective date",
            qboClassName: qboClassName,
            startBillingAt: startBillingAt
          }, service.id));
        },
        visitRepeatableManual: billing => {
          const category = lineItemsPerSection.manual[billing.recurrencePeriod];
          category.push( /*#__PURE__*/_jsx(ManualServiceBillingItem, {
            idx: category.length,
            name: name,
            service: service,
            agreement: agreement,
            controlData: billingControlData,
            costDisplay: costDisplay,
            qboItemName: qboItemName,
            xeroItemName: xeroItemName,
            qboClassName: qboClassName,
            unitCapDisplay: unitCapDisplay,
            invoiceDateInfo: invoiceDateInfo,
            earliestBillableDate: earliestBillableDate
          }, service.id));
        },
        visitOneTimeManual: () => {
          const category = lineItemsPerSection.manual.oneTime;
          category.push( /*#__PURE__*/_jsx(ManualServiceBillingItem, {
            idx: category.length,
            name: name,
            service: service,
            agreement: agreement,
            controlData: billingControlData,
            costDisplay: costDisplay,
            qboItemName: qboItemName,
            xeroItemName: xeroItemName,
            qboClassName: qboClassName,
            unitCapDisplay: unitCapDisplay,
            invoiceDateInfo: invoiceDateInfo,
            earliestBillableDate: earliestBillableDate
          }, service.id));
        },
        visitOneTimeInStages: () => {
          const category = lineItemsPerSection.manual.oneTimeInStages;
          category.push( /*#__PURE__*/_jsx(ManualInStagesBillingItem, {
            idx: category.length,
            name: name,
            service: service,
            agreementId: agreement.id,
            controlData: billingControlData,
            costDisplay: costDisplay,
            qboItemName: qboItemName,
            xeroItemName: xeroItemName,
            qboClassName: qboClassName,
            invoiceDateInfo: invoiceDateInfo,
            earliestBillableDate: earliestBillableDate
          }, service.id));
        }
      });
    });
    billingControlData.credits.sort((a, b) => a.name().toLowerCase().localeCompare(b.name().toLowerCase())).forEach(credit => {
      const index = lineItemsPerSection.recurring.credit.length;
      const disabled = invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected;
      lineItemsPerSection.recurring.credit.push( /*#__PURE__*/_jsx(CreditBillingItem, {
        index: index,
        id: credit.id(),
        name: credit.name(),
        isPaused: credit.isPaused(),
        disabled: disabled,
        tooltipTitle: disabled ? getPauseDisabledTooltip(true) : undefined,
        costDisplay: applyDiscount(credit.totalAmount().toDP(2).abs()),
        agreement: agreement,
        onRefresh: onRefresh
      }, credit.id()));
    });
    return {
      lineItemsPerSection,
      hasItems: !!servicesToDisplay.length || !!billingControlData.credits.length
    };
  }, [services, billingControlData, qboClassesById, qboItemsById, xeroItemsById, invoiceDateInfo, agreement, earliestBillableDate]);
  function onPusherInvoiceEvent(event) {
    if (event.payload.type !== InvoiceEventType.InvoiceIssued) return;
    const {
      RelationshipId,
      ServiceIds
    } = event.payload.invoiceIssued;

    // Why do we do this?
    // After clicking on "bill now" for a manual service, its isBillable state changes to false until the invoice is issued asynchronously.
    // The bill button is disabled in this state.
    // We listen to a pusher event to know when we should refresh the data to see if the service has "isBillable: true" again.
    const shouldRefresh = RelationshipId === agreement.id && agreement.services.some(s => !s.isBillable && ServiceIds.includes(s.id));
    if (shouldRefresh) onRefresh({
      silent: true
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "mgmt",
      eventName: PusherEvent.InvoiceEvent,
      onBind: onBindToUserChannel,
      callback: onPusherInvoiceEvent
    }), hasItems ? /*#__PURE__*/_jsxs("ul", {
      "data-testid": "billing-control",
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.callouts,
        children: [isLinkedToQbo && getAccountingDetails(Accounting.QBO).policy.getUnintegratedServices(services, Accounting.QBO).length > 0 && /*#__PURE__*/_jsx(MapServicesCallout, {
          accounting: Accounting.QBO,
          relationshipId: agreement.id
        }, Accounting.QBO), isLinkedToXero && XERO_INTEGRATION && getAccountingDetails(Accounting.Xero).policy.getUnintegratedServices(services, Accounting.Xero).length > 0 && /*#__PURE__*/_jsx(MapServicesCallout, {
          accounting: Accounting.Xero,
          relationshipId: agreement.id
        }, Accounting.Xero)]
      }), /*#__PURE__*/_jsxs("li", {
        hidden: Object.values(lineItemsPerSection.manual).every(i => i.length === 0),
        "data-testid": "manual-services-group",
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.trigger,
          children: /*#__PURE__*/_jsx(Typography, {
            className: styles.triggerText,
            weight: "bold",
            children: "Manual services"
          })
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "One time",
          hidden: !lineItemsPerSection.manual.oneTime.length,
          "data-testid": "manual-oneTime-section",
          children: lineItemsPerSection.manual.oneTime
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Billed in stages",
          hidden: !lineItemsPerSection.manual.oneTimeInStages.length,
          "data-testid": "manual-oneTime-inStages-section",
          children: lineItemsPerSection.manual.oneTimeInStages
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Weekly",
          hidden: !lineItemsPerSection.manual.weekly.length,
          "data-testid": "manual-weekly-section",
          children: lineItemsPerSection.manual.weekly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Every Two Weeks",
          hidden: !lineItemsPerSection.manual.biweekly.length,
          "data-testid": "manual-biweekly-section",
          children: lineItemsPerSection.manual.biweekly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Monthly",
          hidden: !lineItemsPerSection.manual.monthly.length,
          "data-testid": "manual-monthly-section",
          children: lineItemsPerSection.manual.monthly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Quarterly",
          hidden: !lineItemsPerSection.manual.quarterly.length,
          "data-testid": "manual-quarterly-section",
          children: lineItemsPerSection.manual.quarterly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Yearly",
          hidden: !lineItemsPerSection.manual.yearly.length,
          "data-testid": "manual-yearly-section",
          children: lineItemsPerSection.manual.yearly
        })]
      }), /*#__PURE__*/_jsxs("li", {
        hidden: Object.values(lineItemsPerSection.recurring).every(i => i.length === 0),
        "data-testid": "recurring-services-group",
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.trigger,
          children: /*#__PURE__*/_jsx(Typography, {
            className: styles.triggerText,
            weight: "bold",
            children: "Automatic services"
          })
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "One time, on approval",
          hidden: !lineItemsPerSection.recurring.onApproval.length,
          "data-testid": "recurring-onApproval-section",
          children: lineItemsPerSection.recurring.onApproval
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Weekly",
          hidden: !lineItemsPerSection.recurring.weekly.length,
          "data-testid": "recurring-weekly-section",
          children: lineItemsPerSection.recurring.weekly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "very Two Weeks",
          hidden: !lineItemsPerSection.recurring.biweekly.length,
          "data-testid": "recurring-biweekly-section",
          children: lineItemsPerSection.recurring.biweekly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Monthly",
          hidden: !lineItemsPerSection.recurring.monthly.length,
          "data-testid": "recurring-monthly-section",
          children: lineItemsPerSection.recurring.monthly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Quarterly",
          hidden: !lineItemsPerSection.recurring.quarterly.length,
          "data-testid": "recurring-quarterly-section",
          children: lineItemsPerSection.recurring.quarterly
        }), /*#__PURE__*/_jsx(BillingControlSection, {
          title: "Credit",
          hidden: !lineItemsPerSection.recurring.credit.length,
          "data-testid": "recurring-credit-section",
          children: lineItemsPerSection.recurring.credit
        })]
      })]
    }) : /*#__PURE__*/_jsx("div", {
      "data-testid": "billing-control-empty-state",
      className: styles.emptyState,
      children: /*#__PURE__*/_jsx(Typography, {
        children: "There are no billable services to show"
      })
    })]
  });
}